import { CentralErrorCodes } from "@/services/config/errors";
import { logger } from "@/services/debug/logger.services";
import { CPException } from "@EspaceParkQC/sdk-services-legacy";
import * as Sentry from "@sentry/react";
import i18next from "i18next";
import { isErrorLike, serializeError } from "serialize-error";

/* Constants */

// Default number of retry attempts
const DEFAULT_RETRY_COUNT = 3;

// List of error codes that should prevent retrying
const PREVENT_RETRY_LIST: number[] = [101, 4000];

/*
  Error scopes:
  - CPException: Clicknpark SDK Error
  - Error: JavaScript Error (e.g. TypeError, ReferenceError)
  - AppError: App Specific Error that gets thrown by the application (e.g. validation error)
  - HandledError: Final error object that gets returned to the user with a formatted title and description, if available
*/

export const AppError = class AppError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = "AppError";
    this.code = code;
  }
};

export interface HandledError {
  name?: string;
  code: string;
  handled: boolean;
  message: string;
  formatted: { title: string; description: string };
}

/*
  Retry logic:
  - Prevents retrying on specific error codes
  - Calls an optional callback function before retrying
  - Returns true if the failure count is less than the retry count

  How to use:
  - Add the retry logic to the error handler inside the queryClient (service files)
*/

export const retry = (
  failureCount: number,
  error: unknown,
  extra?: {
    callback?: () => void; // Improved callback typing
    retryCount?: number;
  }
): boolean => {
  if (error instanceof CPException) {
    if (typeof error.code === "number" && PREVENT_RETRY_LIST.includes(error.code)) {
      return false;
    }
  }

  if (extra?.callback) {
    extra.callback();
  }

  return failureCount < (extra?.retryCount || DEFAULT_RETRY_COUNT);
};

// Ref is usually returned from the API to help identify the error
const appendRef = (description: string, ref?: string): string => description + (ref ? ` (ref: ${ref})` : "");

// When the error is not known by the application, the code is appended to the message so we can handle it
const appendUnknownCode = (description: string, code?: string): string => description + (code ? ` (code: ${code})` : "");

/* Helper function to format errors */
const formatError = (code: string, message: string, ref?: string): HandledError => {
  if (i18next.exists(`errors:${code}`) && code !== "UNKNOWN_CPEXCEPTION_CODE") {
    return {
      code,
      message,
      handled: true,
      formatted: {
        title: i18next.t(`errors:${code}.title`),
        description: appendRef(i18next.t(`errors:${code}.description`), ref),
      },
    };
  } else {
    return {
      code,
      message,
      handled: false,
      formatted: {
        title: i18next.t("errors:unhandled.title"),
        description: appendRef(appendUnknownCode(message, code), ref),
      },
    };
  }
};

/*

  Error handler:
  - Handles different types of errors
  - Returns a formatted error object with a title and description, if available

  How to use:
  - In any try catch block, call handleError(error) to get the formatted error object
  - you can display it using a toast or a state variable, whatever, but it will always be formatted

*/

export const handleError = (error: unknown): HandledError => {
  Sentry.captureException(error);

  if (error instanceof CPException) {
    logger.error("CPException", error);
    return formatError(error.code?.toString() || "UNKNOWN_CPEXCEPTION_CODE", error.message, error.ref);
  }

  // App Specific Error
  else if (error instanceof AppError) {
    logger.error("AppError", error);
    return formatError(error.code, error.message);
  }

  // JavaScript Error (make sure to check that last since CPException and AppError are also instances of Error)
  else if (error instanceof Error) {
    logger.error("Error", error);
    return formatError("UNHANDLED_ERROR", error.message);
  }

  // Unhandled Error
  else {
    // Must have at least 'name', 'message', and 'stack' properties
    if (isErrorLike(error)) {
      logger.error("Unhandled Error (error like)", error);
      return formatError(error?.code || "UNHANDLED_ERROR", error?.message);
    } else {
      // Converts to a plain object
      const serializedError = serializeError(error);

      // Converts to a string
      const stringifiedError = JSON.stringify(serializedError);

      logger.error("Unhandled Error", stringifiedError);
      return formatError("UNHANDLED_ERROR", stringifiedError);
    }
  }
};

export const isAuthError = (error: unknown): boolean => {
  if (error instanceof CPException) {
    const authCodes = new Set([
      CentralErrorCodes.INVALID_TOKEN,
      CentralErrorCodes.EXPIRED_TOKEN,
      CentralErrorCodes.REVOKED_TOKEN,
      CentralErrorCodes.UNKNOWN_AUTH_TOKEN_ERROR,
    ]);

    return authCodes.has(error?.code?.toString() as CentralErrorCodes);
  }

  return false;
};
